import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import axios from 'axios';
import SectionHeader from '../../components/SectionHeader/SectionHeader';
import './DeveloperPortalLayout.css';

interface VenueMapping {
  id: string;
  venue_id: string;
  oauth_client_id: string;
  internal_id: string | null;
  venue_name: string;
  created_at: string;
  updated_at: string;
}

interface OAuthClient {
  id: string;
  client_id: string;
  name: string;
}

interface VenueMappingsProps {
  onNavigate: (section: string) => void;
}

const VenueMappings: React.FC<VenueMappingsProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [clients, setClients] = useState<OAuthClient[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [mappings, setMappings] = useState<VenueMapping[]>([]);
  const [filteredMappings, setFilteredMappings] = useState<VenueMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [filterOption, setFilterOption] = useState<'all' | 'with-id' | 'without-id'>('all');
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Load user's OAuth clients
  const loadClients = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${config.API_BASE_URL}/oauth/clients`);
      setClients(response.data);
      if (response.data.length > 0 && !selectedClientId) {
        setSelectedClientId(response.data[0].client_id);
      }
    } catch (err) {
      console.error('Error loading OAuth clients:', err);
    }
  };

  // Load mappings for selected client
  const loadMappings = async () => {
    if (!selectedClientId) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/oauth/clients/${selectedClientId}/venue-identifiers`
      );
      setMappings(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error loading venue mappings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadClients();
    }
  }, [user]);

  useEffect(() => {
    if (selectedClientId) {
      loadMappings();
    }
  }, [selectedClientId]);

  // Filter and search mappings
  useEffect(() => {
    let filtered = [...mappings];

    // Apply filter option
    if (filterOption === 'with-id') {
      filtered = filtered.filter(m => m.internal_id !== null && m.internal_id !== '');
    } else if (filterOption === 'without-id') {
      filtered = filtered.filter(m => m.internal_id === null || m.internal_id === '');
    }

    // Apply search term
    if (searchTerm) {
      filtered = filtered.filter(m => 
        m.venue_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.internal_id && m.internal_id.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredMappings(filtered);
  }, [mappings, searchTerm, filterOption]);

  const handleEdit = (mapping: VenueMapping) => {
    setEditingId(mapping.id);
    setEditValue(mapping.internal_id || '');
  };

  const handleSave = async (mapping: VenueMapping) => {
    try {
      await axios.put(
        `${config.API_BASE_URL}/oauth/clients/${selectedClientId}/venue-identifiers/${mapping.id}`,
        {
          internal_id: editValue || null
        }
      );
      setEditingId(null);
      loadMappings(); // Reload to get fresh data
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error updating mapping');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleClear = async (mapping: VenueMapping) => {
    try {
      await axios.put(
        `${config.API_BASE_URL}/oauth/clients/${selectedClientId}/venue-identifiers/${mapping.id}`,
        {
          internal_id: null
        }
      );
      setEditingId(null);
      setEditValue('');
      loadMappings(); // Reload to get fresh data
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error clearing mapping');
    }
  };

  const getMappingsWithIds = () => mappings.filter(m => m.internal_id !== null && m.internal_id !== '').length;

  if (!user) {
    return <div>Please log in to view venue mappings.</div>;
  }

  return (
    <div className="applications-container">
      <SectionHeader
        title="Venue ID Mappings"
        description="Map your internal restaurant/venue IDs to PlatefullAI venues for seamless integration"
        rightContent={
          clients.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ color: '#fff', fontSize: '14px', fontWeight: '500' }}>
                OAuth App:
              </label>
              <select
                className="form-input"
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                style={{ width: '250px', margin: 0 }}
              >
                {clients.map(client => (
                  <option key={client.client_id} value={client.client_id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
          ) : undefined
        }
      />

      {clients.length === 0 ? (
        <div className="empty-state">
          <h3>No OAuth Applications</h3>
          <p>Create an OAuth application first to manage venue mappings.</p>
          <button className="primary-btn" onClick={() => onNavigate('applications')}>
            Go to Applications
          </button>
        </div>
      ) : (
        <>

          {/* Stats */}
          <div className="section">
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <div className="stat-card">
                <div className="stat-label">Total Venues</div>
                <div className="stat-value">{mappings.length}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">With Internal ID</div>
                <div className="stat-value">{getMappingsWithIds()}</div>
              </div>
            </div>
            <button
              onClick={() => setShowHelpModal(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#26A69A',
                cursor: 'pointer',
                fontSize: '14px',
                textDecoration: 'none',
                padding: '10px 0',
                marginTop: '10px'
              }}
            >
              What is this?
            </button>
          </div>

          {/* Search and Filter */}
          <div className="section">
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Search by venue name or internal ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ flex: 1 }}
              />
              <select
                className="form-input"
                value={filterOption}
                onChange={(e) => setFilterOption(e.target.value as any)}
                style={{ width: '200px' }}
              >
                <option value="all">All Venues</option>
                <option value="with-id">With Internal ID</option>
                <option value="without-id">Without Internal ID</option>
              </select>
            </div>
          </div>

          {/* Mappings Table */}
          <div className="section">
            {loading ? (
              <div className="loading-state">Loading mappings...</div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : filteredMappings.length === 0 ? (
              <div className="empty-state">
                <p>No venues match your search criteria.</p>
              </div>
            ) : (
              <div className="table-container" style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Venue Name</th>
                      <th style={{ textAlign: 'left' }}>Internal ID</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMappings.map((mapping) => (
                      <tr key={mapping.id}>
                        <td>
                          <strong>{mapping.venue_name}</strong>
                        </td>
                        <td>
                          {editingId === mapping.id ? (
                            <input
                              type="text"
                              className="form-input"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              placeholder="Enter internal ID (or leave empty to clear)..."
                              autoFocus
                              style={{ minWidth: '200px' }}
                            />
                          ) : (
                            <span style={{ color: mapping.internal_id ? '#333' : '#999', fontStyle: mapping.internal_id ? 'normal' : 'italic' }}>
                              {mapping.internal_id || 'Not set'}
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {mapping.internal_id ? (
                            <span className="status-badge status-active">Active</span>
                          ) : (
                            <span className="status-badge">Inactive</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {editingId === mapping.id ? (
                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                              <button
                                className="secondary-btn"
                                onClick={() => handleSave(mapping)}
                                style={{ padding: '5px 15px', fontSize: '13px' }}
                              >
                                Save
                              </button>
                              {mapping.internal_id && (
                                <button
                                  className="secondary-btn"
                                  onClick={() => handleClear(mapping)}
                                  style={{ padding: '5px 15px', fontSize: '13px', background: '#f59e0b', borderColor: '#f59e0b' }}
                                  title="Clear internal ID"
                                >
                                  Clear
                                </button>
                              )}
                              <button
                                className="danger-btn"
                                onClick={handleCancel}
                                style={{ padding: '5px 15px', fontSize: '13px' }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              className="secondary-btn"
                              onClick={() => handleEdit(mapping)}
                              style={{ padding: '5px 15px', fontSize: '13px' }}
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </>
      )}

      {/* Help Modal */}
      {showHelpModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowHelpModal(false)}
        >
          <div
            style={{
              backgroundColor: '#2a2a2a',
              border: '1px solid #444',
              borderRadius: '8px',
              padding: '30px',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#fff' }}>💡 How it works</h2>
              <button
                onClick={() => setShowHelpModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  lineHeight: '1'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ color: '#ddd', lineHeight: '1.6' }}>
              <h3 style={{ color: '#4CAF50', marginTop: '0' }}>Automatic Mapping</h3>
              <p>All your venues are automatically linked to your OAuth applications with no setup required.</p>
              
              <h3 style={{ color: '#4CAF50', marginTop: '20px' }}>Adding Internal IDs</h3>
              <p>Add your internal IDs (e.g., from your restaurant management system) to enable widget integration with your existing systems.</p>
              
              <h3 style={{ color: '#4CAF50', marginTop: '20px' }}>Using in the Widget</h3>
              <p>Pass <code style={{ backgroundColor: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '3px', color: '#4CAF50' }}>internalVenueId</code> in the widget to automatically track visits to the correct venue:</p>
              <pre style={{ 
                backgroundColor: '#1a1a1a', 
                padding: '15px', 
                borderRadius: '4px', 
                overflow: 'auto',
                fontSize: '13px',
                border: '1px solid #333'
              }}>
{`PlatefullAI.renderSignInButton('container', {
  internalVenueId: 'your-restaurant-123',
  // ... other options
});`}
              </pre>
              
              <h3 style={{ color: '#4CAF50', marginTop: '20px' }}>Optional</h3>
              <p>Leave internal ID empty if you don't need external ID mapping for a venue. You can still use the venue's UUID directly.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VenueMappings;

